import { Globe } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEventLinkPreviewContext } from "@/contexts/EventLinkPreviewContext";
import { useLinkPreview } from "@/hooks/useLinkPreview";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { EventLinkPreview } from "./EventLinkPreview";

interface LinkPreviewProps {
  url: string;
  onEventClick?: (eventData: EventApiResponse) => void;
  titleOverride?: string;
}

export const LinkPreview: React.FC<LinkPreviewProps> = ({
  url,
  onEventClick,
  titleOverride,
}) => {
  const { metadata, isLoading, error } = useLinkPreview(url);
  const [faviconError, setFaviconError] = useState(false);
  const { t } = useTranslation();
  const { onEventClick: contextOnEventClick, currentStudyGroupId } =
    useEventLinkPreviewContext();
  const effectiveOnEventClick = onEventClick ?? contextOnEventClick;
  const hostname = useMemo(() => {
    try {
      if (url.startsWith("/") && !url.startsWith("//")) {
        return typeof window === "undefined" ? "" : window.location.hostname;
      }

      return new URL(url).hostname;
    } catch {
      return "";
    }
  }, [url]);

  const handleFaviconError = () => {
    setFaviconError(true);
  };

  if (isLoading) {
    return <span className="text-sm text-gray-500">{t("common.loading")}</span>;
  }

  if ((error || !metadata) && titleOverride) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block overflow-hidden rounded-lg border border-gray-300 bg-gray-50 !no-underline transition-colors duration-200 hover:bg-gray-100 hover:!no-underline"
      >
        <div className="flex items-start gap-3 px-3 py-2">
          <Globe className="mt-0.5 size-4 shrink-0 text-gray-400" />
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 truncate text-xs font-medium text-gray-600">
              {hostname || "RuleWatcher"}
            </div>
            <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-blue-700">
              {titleOverride}
            </h3>
            <p className="mt-1 truncate text-xs text-gray-500">{url}</p>
          </div>
        </div>
      </a>
    );
  }

  if (error || !metadata) {
    // エラーがある場合やメタデータがない場合は何も表示しない
    return null;
  }

  // イベント専用のプレビュー表示
  if (metadata.isEvent && metadata.eventData) {
    return (
      <EventLinkPreview
        url={url}
        event={metadata.eventData}
        image={metadata.image}
        currentStudyGroupId={currentStudyGroupId}
        onEventClick={effectiveOnEventClick}
      />
    );
  }

  // 通常のリンクプレビュー表示
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block overflow-hidden rounded-lg border border-gray-300 bg-gray-50 !no-underline transition-colors duration-200 hover:bg-gray-100 hover:!no-underline"
    >
      <div
        className={`flex items-start justify-between gap-3 px-3 pt-2 ${
          metadata.description ? "pb-0" : "pb-1"
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-1.5">
            {metadata.favicon && !faviconError ? (
              <img
                src={metadata.favicon}
                alt={
                  metadata.siteName
                    ? `${metadata.siteName} favicon`
                    : "Website favicon"
                }
                className="size-4 shrink-0"
                onError={handleFaviconError}
              />
            ) : (
              <Globe className="size-4 shrink-0 text-gray-400" />
            )}
            <span className="truncate text-xs font-medium text-gray-600">
              {metadata.siteName || hostname || "Website"}
            </span>
          </div>
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-blue-700">
            {titleOverride || metadata.title || "Link Preview"}
          </h3>
          {metadata.description && (
            <p className="mt-1 line-clamp-3 text-sm leading-snug text-gray-700">
              {metadata.description}
            </p>
          )}
        </div>
        {metadata.image && (
          <div className="hidden size-16 shrink-0 items-center justify-center overflow-hidden rounded bg-white sm:flex md:size-20">
            <img
              src={metadata.image}
              alt={metadata.title || metadata.description || "Preview image"}
              className="size-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.parentElement?.remove();
              }}
            />
          </div>
        )}
      </div>
    </a>
  );
};
